import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  ErrorMessage,
  Icon,
} from 'anf-core-react';

import {
  useSearchParams,
  useNavigate,
  useParams,
} from 'react-router';

import {
  useLazyQuery,
  useQuery,
} from '@apollo/client';

import { GET_CONFIRMATION_TMNTS } from '../../gql/tmnts.gql';
import SUBMIT_RETURN_CANCEL from '../../gql/order-cancel.gql';

import CancelModal from '../../components/cancel-modal/cancel-modal';
import TmntText from '../../components/tmnt-text/tmnt-text';
import ProductRecommendations from '../../components/product-recommendations/product-recs';
import Loading from '../../components/loading/loading';
import NextStepsSection from './next-steps-section';
import OrderSummary from './order-summary';
import ActionButtons from './action-buttons';

import TmntContext from '../../context/tmnt';
import getSignifydSessionID from '../../utils/signifyd-session-id';
import { useGetDigitalDataFlagValue } from '../../utils/digital-data-flag';
import useGetReturnedOrder from '../../hooks/useGetReturnedOrder';
import getHeadlineTmnt from './get-headline-tmnt';
import triggerSurvey from '../../utils/trigger-survey';

/**
 * Exchange Confirmation View Component
 * 
 * This component handles the display of exchange/return confirmation details.
 * It manages order loading, cancellation, and renders the appropriate UI sections.
 * 
 * @returns {JSX.Element} The exchange confirmation view
 */
export default function ExchangeConfirmationView() {
  const navigate = useNavigate();
  const { storeId, orderId } = useParams();
  const [isCancel, setIsCancel] = useState(false);
  const [searchParams] = useSearchParams();
  const surveyTriggered = useRef(false);

  const usingProductRecs = !!useGetDigitalDataFlagValue('ful-dre-2-0-product-recs', false);
  const usingBubbleRecs = !!useGetDigitalDataFlagValue('ful-dre-2-0-bubble-recs', false);
  const usingMfeQuickview = !!useGetDigitalDataFlagValue('ful-dre-2-0-mfe-quickview', false);

  // TMNTs
  const {
    data: tmnts,
  } = useQuery(GET_CONFIRMATION_TMNTS);

  // The first time the user navigates to this page, we clear sessionStorage.
  // If the user ever navigates to this page again, we send them to the beginning of the flow.
  useEffect(() => {
    const selectedProducts = window.sessionStorage.getItem(`${orderId}-p`);

    if (!selectedProducts) {
      navigate(`/store/${storeId}/exchange/${orderId}`);
      return;
    }

    window.sessionStorage.removeItem(`${orderId}-p`);
    window.sessionStorage.removeItem(`${orderId}-c`);
    window.sessionStorage.removeItem(`${orderId}-s`);
    window.sessionStorage.removeItem(`${orderId}-r`);
    window.sessionStorage.removeItem(`${orderId}-rc`);
    window.sessionStorage.removeItem(`${orderId}-method`);
    window.sessionStorage.removeItem(`${orderId}-rm`);

    // Always Scroll To Window Top
    window.scrollTo(0, 0);
  }, [navigate, orderId, storeId]);

  // Use the new hook for order loading logic
  const {
    returnedOrder,
    confirmationDetails,
    loadingFailure,
    labelOrderId,
    queryCount,
    isReadyToDisplay,
  } = useGetReturnedOrder();

  // Cancel Return or Exchange
  const [submitReturnCancel, {
    data: cancelData,
  }] = useLazyQuery(SUBMIT_RETURN_CANCEL);

  const handleReturnCancel = useCallback(() => {
    const cancelOrderId = searchParams.get('label');
    const signifydSessionId = getSignifydSessionID(document);

    submitReturnCancel({
      variables: {
        cancelDetail: {
          orderId: cancelOrderId,
          email: returnedOrder.orderDetails.email,
          changeReasonCode: 'USER_CANCEL',
          signifydSessionId,
        },
      },
    });
  }, [submitReturnCancel, searchParams, returnedOrder]);

  const {
    returnMethod: carrier,
  } = returnedOrder;

  const {
    errorMsgTmnt,
    orderDetailUrl,
    exchangeHeadlineTmnt,
  } = confirmationDetails;

  const isReturn = searchParams.get('isReturn') === '1';
  const isExchange = searchParams.get('isExchange') === '1';
  const headlineTmnt = getHeadlineTmnt(exchangeHeadlineTmnt, isReturn, isExchange);

  // Upon cancel, reload the page to display the order detail
  useEffect(() => {
    if (!cancelData) return;
    window.location.href = orderDetailUrl;
  }, [cancelData, orderDetailUrl]);

  useEffect(() => {
    if (!tmnts) return;
    triggerSurvey(surveyTriggered);
  }, [tmnts]);
 
  return (
    <TmntContext.Provider value={tmnts}>
      {tmnts && (
        <div className="exchange-confirmation-view" data-testid="exchange-confirmation-view">
          <div className="exchange-confirmation-view-wrapper">
            <div className="mt-1 text-center">
              <Icon icon="checkmark-circle" size="l" />
            </div>

            <h3 className="h1">
              <div className="exchange-headline" data-testid="exchange-headline">
                <TmntText className="headline-text" k={headlineTmnt} />
              </div>
            </h3>

            <div
              data-testid="returns-exchange-survey-wrapper"
              className="returns-exchange-survey-wrapper"
            ></div>

            {((!isReadyToDisplay)
              && !loadingFailure)
              && (
              <div className="loading-state-container" data-testid="loading-state-container">
                <Loading hasOverlay={false} size={4} />
              </div>
              )}

            {(isReadyToDisplay) && (
              <>
                <OrderSummary
                  confirmationDetails={confirmationDetails}
                  returnedOrder={returnedOrder}
                  labelOrderId={labelOrderId}
                />
                <NextStepsSection
                  tmnts={tmnts}
                  carrier={carrier}
                  confirmationDetails={confirmationDetails}
                  returnedOrder={returnedOrder}
                />
              </>
            )}

            {loadingFailure && (
              <ErrorMessage classList="mt-5">
                <TmntText k={errorMsgTmnt} />
              </ErrorMessage>
            )}

            <ActionButtons
              confirmationDetails={confirmationDetails}
              returnedOrder={returnedOrder}
              loadingFailure={loadingFailure}
              queryCount={queryCount}
              onCancelClick={() => setIsCancel(true)}
              isCancelDisabled={!returnedOrder?.orderDetails || !carrier}
              isReadyToDisplay={isReadyToDisplay}
            />
          </div>

          <CancelModal isOpen={isCancel} setOpen={setIsCancel} handleSubmit={handleReturnCancel} />
          <ProductRecommendations
            usingProductRecs={usingProductRecs}
            usingBubbleRecs={usingBubbleRecs}
            usingMfeQuickview={usingMfeQuickview}
          />
        </div>
      )}
    </TmntContext.Provider>
  );
}

// This component doesn't accept any props as it's a self-contained view
ExchangeConfirmationView.propTypes = {};
